/**
 * Iter-296 — Client Calendar Sync Logs.
 *
 * Read-only audit trail of every sync run for a client's connected
 * calendars. Each log row shows trigger (manual/scheduled), counts of
 * new / updated / cancelled events, duplicates + conflicts surfaced,
 * status, and any error message.
 *
 * Used by the client to confirm "yes, I ran a sync at 9:14am and 3
 * events came in" and by support to debug failed syncs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  ArrowLeft, Loader2, RefreshCw, CheckCircle2, AlertTriangle, ScrollText,
} from 'lucide-react';

function fmtDuration(start, end) {
  if (!end) return '…';
  const ms = new Date(end) - new Date(start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

function StatusBadge({ status }) {
  if (status === 'success') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
        <CheckCircle2 className="w-3 h-3 mr-1" />Success
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge className="bg-rose-100 text-rose-800 border-rose-300">
        <AlertTriangle className="w-3 h-3 mr-1" />Failed
      </Badge>
    );
  }
  if (status === 'partial') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-300">
        Partial
      </Badge>
    );
  }
  return <Badge variant="secondary">{status}</Badge>;
}

export default function ClientCalendarSyncLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/client/calendar/sync-logs?limit=100');
      setLogs(res.data.logs || []);
    } catch {
      toast.error('Failed to load sync logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-calendar-sync-logs-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-slate-900 truncate">Sync Logs</h1>
            <p className="text-xs text-slate-500 truncate">Audit trail of every calendar sync run.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        {!loading && logs.length === 0 && (
          <div className="text-center py-16 text-sm text-slate-500" data-testid="empty-state-no-logs">
            <ScrollText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p>No sync runs yet.</p>
            <p className="text-xs mt-1">Run a manual sync from the Integrations page to populate this list.</p>
          </div>
        )}
        {!loading && logs.map((log) => (
          <Card key={log.id} data-testid={`log-row-${log.id}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                <div className="text-sm font-medium text-slate-800">
                  {new Date(log.started_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={log.status} />
                  <Badge variant="outline" className="text-[10px]">{log.trigger || 'manual'}</Badge>
                  <span className="text-[11px] text-slate-500">{fmtDuration(log.started_at, log.finished_at)}</span>
                </div>
              </div>
              <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5">
                <span><b>{log.events_new}</b> new</span>
                <span><b>{log.events_updated}</b> updated</span>
                <span><b>{log.events_cancelled}</b> cancelled</span>
                <span><b>{log.duplicates_found}</b> duplicates</span>
                <span><b>{log.conflicts_found}</b> conflicts</span>
              </div>
              {log.error_message && (
                <p className="text-xs text-rose-700 mt-1.5 truncate" title={log.error_message}>
                  {log.error_message}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
